import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, Card, Input } from '@bruno/ui';
import type { PublicCaptchaConfigDTO } from '@bruno/shared';
import { useAuth } from '../auth/AuthContext';
import { useCapCaptcha } from '../auth/useCapCaptcha';
import { getCaptchaConfig, resendVerification } from '../api/auth';
import { CaptchaStatusBadge } from '../components/CaptchaStatusBadge';

export function SignupPage() {
  const { user, signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [captchaCfg, setCaptchaCfg] = useState<PublicCaptchaConfigDTO | null>(null);
  /** Set to the submitted address once signup succeeds; flips the page
   *  into the "confirm your email" state. */
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getCaptchaConfig()
      .then((cfg) => {
        if (!cancelled) setCaptchaCfg(cfg);
      })
      .catch(() => {
        if (!cancelled) setCaptchaCfg({ enabled: false, instanceUrl: '', siteKey: '' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  if (user) return <Navigate to="/websites" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) {
        throw new Error('Captcha verification failed. Please try again.');
      }
      const res = await signup(email, password, name || undefined, token ?? undefined);
      setPendingEmail(res.email);
    } catch (err) {
      if (captchaCfg?.enabled) captcha.reset();
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  }

  if (pendingEmail) {
    return <CheckInboxCard email={pendingEmail} captchaCfg={captchaCfg} />;
  }

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Card className="w-full max-w-md">
        <div className="mb-6">
          <h1 className="text-xl font-semibold">Create your Bruno account</h1>
          <p className="text-sm text-slate-500">Start adding AI guidance to your websites in minutes.</p>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <Button type="submit" loading={loading}>
            Create account
          </Button>
          {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
        </form>
        <div className="mt-4 text-center text-sm text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="text-indigo-600 hover:underline">
            Sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}

/**
 * Post-signup confirmation screen. The account exists but is unverified;
 * the user must click the emailed link before they can sign in. Offers a
 * resend with a fresh captcha token when captcha is enabled.
 */
function CheckInboxCard({
  email,
  captchaCfg,
}: {
  email: string;
  captchaCfg: PublicCaptchaConfigDTO | null;
}) {
  // Use a dedicated captcha instance rather than the signup form's: the
  // signup request already consumed that token (Cap tokens are single-use),
  // so reusing it for the resend would always be rejected. This instance
  // pre-solves a fresh token in the background as soon as the card mounts.
  const captcha = useCapCaptcha(captchaCfg ?? undefined);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  async function resend() {
    setResendState('sending');
    try {
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) throw new Error('captcha unavailable');
      await resendVerification(email, token ?? undefined);
      setResendState('sent');
    } catch {
      // Failed/spent captcha or a network error — discard the token,
      // pre-solve a fresh one, and let the user retry instead of falsely
      // claiming the email was sent.
      if (captchaCfg?.enabled) captcha.reset();
      setResendState('error');
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Card className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-indigo-50">
          <svg
            className="h-6 w-6 text-indigo-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4 4h16v16H4z" />
            <path d="m22 6-10 7L2 6" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold">Confirm your email</h1>
        <p className="mt-2 text-sm text-slate-600">
          We sent a confirmation link to <span className="font-medium text-slate-900">{email}</span>.
          Click it to activate your account — you&apos;ll be signed in automatically.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          The link expires in 24 hours. Be sure to check your spam folder.
        </p>

        <div className="mt-6 flex flex-col items-center gap-2">
          {resendState === 'sent' ? (
            <div className="text-sm text-emerald-600">
              Sent. If an account needs confirming, a new link is on its way.
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={resend}
              loading={resendState === 'sending'}
            >
              Resend confirmation email
            </Button>
          )}
          {resendState === 'error' ? (
            <div className="text-sm text-red-600">Could not resend just now. Please try again.</div>
          ) : null}
          {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
        </div>

        <div className="mt-6 border-t border-slate-100 pt-4 text-sm text-slate-500">
          Already confirmed?{' '}
          <Link to="/login" className="text-indigo-600 hover:underline">
            Sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
